import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getLastAuthProvider,
  setLastAuthProvider,
  type AuthProviderName,
} from "../services/lastAuthProvider";
import { createLogger } from "../utils/logger";

const authButtonsLogger = createLogger({ prefix: "OAuthButtons" });

/**
 * Error codes that indicate the user intentionally cancelled the OAuth popup.
 * These should NOT show a spinner overlay or error message to the user.
 */
const POPUP_CANCEL_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/popup-timeout",
  "auth/cancelled-popup-request",
]);

/**
 * Check if an error is a popup cancellation (user closed popup or it timed out).
 * These are expected user actions, not real errors.
 */
function isPopupCancellation(error: unknown): boolean {
  if (error instanceof Error && "code" in error) {
    return POPUP_CANCEL_CODES.has((error as { code: string }).code);
  }
  return false;
}

interface OAuthButtonsProps {
  onSigningInChange?: (isSigningIn: boolean) => void;
}

export const OAuthButtons: React.FC<OAuthButtonsProps> = ({
  onSigningInChange,
}) => {
  const {
    signInWithGoogle,
    signInWithApple,
    signInWithMicrosoft,
    loading,
    clearError,
  } = useAuth();
  const [signingIn, setSigningIn] = useState(false);

  // Generic OAuth handler that manages button loading state and popup cancellation.
  //
  // Flow:
  //   1. Disable buttons immediately (signingIn = true)
  //   2. Wait for popup to complete — NO full-page overlay yet
  //   3. On success: store provider + show full-page overlay (profile creation phase)
  //   4. On popup cancellation: silently reset — no spinner, no error
  //   5. On real error: let AuthContext display the error
  const handleOAuthSignIn = async (
    signInFn: () => Promise<void>,
    provider: AuthProviderName
  ) => {
    setSigningIn(true);
    try {
      await signInFn();
      // Remember which provider was used for returning-user hint
      setLastAuthProvider(provider);
      // OAuth succeeded — show overlay while profile loads and redirect happens.
      // Do NOT reset overlay here: the component will unmount on navigation.
      onSigningInChange?.(true);
    } catch (error) {
      if (isPopupCancellation(error)) {
        // User closed popup or it timed out — silently reset.
        // Clear any error set by AuthContext since this isn't a real error.
        clearError();
      } else {
        // Real error — already handled by AuthContext (logged + shown to user).
        // Defensive: log here too in case AuthContext logging failed.
        authButtonsLogger.error(
          `${provider} sign-in error caught in component (defensive logging)`,
          { error }
        );
      }
      // Only reset button/overlay state on error. On success, the component
      // unmounts during redirect — resetting would cause a visible flicker.
      setSigningIn(false);
      onSigningInChange?.(false);
    }
  };

  const handleGoogleSignIn = () =>
    handleOAuthSignIn(signInWithGoogle, "google");
  const handleAppleSignIn = () => handleOAuthSignIn(signInWithApple, "apple");
  const handleMicrosoftSignIn = () =>
    handleOAuthSignIn(signInWithMicrosoft, "microsoft");

  // Disable buttons when: determining auth or signing in
  const isDisabled = loading || signingIn;

  const PROVIDER_DISPLAY_NAMES: Record<AuthProviderName, string> = {
    google: "Google",
    apple: "Apple",
    microsoft: "Microsoft",
  };

  const lastProvider = getLastAuthProvider();

  return (
    <div className="oauth-buttons" id="oauth-buttons">
      {lastProvider && (
        <p className="provider-hint">
          Last signed in with {PROVIDER_DISPLAY_NAMES[lastProvider]}
        </p>
      )}
      {/* Google Sign In Button */}
      <button
        onClick={handleGoogleSignIn}
        disabled={isDisabled}
        className="oauth-btn oauth-btn-google"
        id="sign-in-google"
        aria-label="Sign in with Google"
      >
        <svg
          className="oauth-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span>Sign in with Google</span>
      </button>

      {/* Apple Sign In Button */}
      <button
        onClick={handleAppleSignIn}
        disabled={isDisabled}
        className="oauth-btn oauth-btn-apple"
        id="sign-in-apple"
        aria-label="Sign in with Apple"
      >
        <svg
          className="oauth-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M17.05 13.5c-.91 2.18.12 4.75 2.85 5.65.32-1.14.3-2.35-.08-3.47-.75-2.21-2.92-3.74-5.23-3.6-.3 2.23.41 4.47 2.46 5.42m-5.98.02c2.03-.94 2.75-3.19 2.45-5.42-2.31-.14-4.48 1.39-5.23 3.6-.38 1.12-.4 2.33-.08 3.47 2.73-.9 3.76-3.47 2.86-5.65M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8"
          />
        </svg>
        <span>Sign in with Apple</span>
      </button>

      {/* Microsoft Sign In Button */}
      <button
        onClick={handleMicrosoftSignIn}
        disabled={isDisabled}
        className="oauth-btn oauth-btn-microsoft"
        id="sign-in-microsoft"
        aria-label="Sign in with Microsoft"
      >
        <svg
          className="oauth-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#00A4EF"
            d="M11.4 24H0V12.6h11.4V24M24 24H12.6V12.6H24V24M11.4 11.4H0V0h11.4v11.4M24 11.4H12.6V0H24v11.4"
          />
        </svg>
        <span>Sign in with Microsoft</span>
      </button>
    </div>
  );
};
